import { useTranslation } from 'react-i18next'
import { SettlementTableModel } from '../../../api/Argentia/endpoints/settlements/model/settlements'
import { EnumEstadoCiclo } from '../../../api/oData/enums/enumEstadoCiclo'
import { EnumEstadoLiquidacion } from '../../../api/oData/enums/enumEstadoLiquidacion'
import { enumValueToKey } from '../../../helpers'
import { useConfirmationDialog } from '../../../hooks'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { InformationDialogs } from '../../../model/enums/informationDialogs'
import { fetchInvoiceCycleExecution } from '../../invoiceCycles/invoiceCycles.service'
import { validateSettlementsState } from '../settlements.service'

/**
 * Hook que comprueba que las liquidaciones elegidas sigan en el estado esperado antes de lanzar un Lote.
 * Si otro Lote las está procesando (o ya las procesó) avisa de que el back las excluirá del nuevo Lote.
 *
 * @param executionCycleId - Ejecución del ciclo sobre la que se lanza el Lote
 * @returns Función confirmSettlementsBatchLaunch
 */
export const useConfirmSettlementsBatchLaunch = (executionCycleId: number) => {
  const [t] = useTranslation(['confirmDialogs'])
  const { getConfirmation, getInformation, closeConfirmation } = useConfirmationDialog()

  /**
   * Con conflictos, el aviso sustituye a la confirmación genérica de la acción (un único modal).
   * @param rows - Liquidaciones elegidas (sin filas no hay nada que validar)
   * @param expectedState - Estado en el que deberían estar las liquidaciones para esta acción
   * @param genericConfirmation - Confirmación habitual de la acción (si la tiene), para cuando no hay conflictos
   * @returns true si se puede continuar con el lanzamiento del Lote
   */
  const confirmSettlementsBatchLaunch = async (
    rows: SettlementTableModel[],
    expectedState: EnumEstadoLiquidacion,
    genericConfirmation?: ConfirmationDialogs
  ): Promise<boolean> => {
    let total = rows.length
    let excluded = 0

    if (rows.length > 0) {
      const response = await validateSettlementsState({
        executionCycleId,
        settlementIds: rows.map(row => row.id),
        expectedState
      })
      // La validación es informativa (el back descarta igualmente): si falla no se bloquea la acción
      if (response.success && response.data) {
        total = response.data.total
        excluded = response.data.invalid.length
      }
    }

    if (excluded === 0) {
      if (genericConfirmation !== undefined) {
        await getConfirmation(genericConfirmation).then(() => {
          closeConfirmation()
        })
      }
      return true
    }

    const validCount = total - excluded
    if (validCount <= 0) {
      await getInformation(InformationDialogs.INVOICE_CYCLE_SETTLEMENTS_NONE_AVAILABLE).then(() => {
        closeConfirmation()
      })
      return false
    }

    await getConfirmation(
      ConfirmationDialogs.INVOICE_CYCLE_SETTLEMENTS_IN_OTHER_BATCHES,
      t(
        `confirmDialogs:${enumValueToKey(ConfirmationDialogs, ConfirmationDialogs.INVOICE_CYCLE_SETTLEMENTS_IN_OTHER_BATCHES)}.message`,
        { total, excluded, count: validCount }
      )
    ).then(() => {
      closeConfirmation()
    })
    return true
  }

  /**
   * Para lotes masivos (sin liquidaciones concretas que validar): si la ejecución está Procesando
   * avisa de que las liquidaciones ya vinculadas a otros Lotes se descartarán del nuevo.
   * Con la ejecución Procesando el aviso se antepone al mensaje de la confirmación habitual (un único modal).
   * Igual que getConfirmation, resuelve al poder continuar y no resuelve si el usuario cancela.
   * @param genericConfirmation - Confirmación habitual de la acción (si no la tiene, solo se muestra el aviso cuando procede)
   */
  const confirmMassiveBatchLaunch = async (genericConfirmation?: ConfirmationDialogs) => {
    const response = await fetchInvoiceCycleExecution(executionCycleId)
    // La comprobación es informativa (el back descarta igualmente): si falla no se bloquea la acción
    const isExecutionProcessing =
      response.success && response.data?.status === EnumEstadoCiclo.Procesando

    if (!isExecutionProcessing) {
      if (genericConfirmation !== undefined) {
        await getConfirmation(genericConfirmation).then(() => {
          closeConfirmation()
        })
      }
      return
    }

    const warningDialogKey = enumValueToKey(
      ConfirmationDialogs,
      ConfirmationDialogs.INVOICE_CYCLE_SETTLEMENTS_IN_OTHER_BATCHES
    )

    if (genericConfirmation === undefined) {
      await getConfirmation(
        ConfirmationDialogs.INVOICE_CYCLE_SETTLEMENTS_IN_OTHER_BATCHES,
        t(`confirmDialogs:${warningDialogKey}.messageProcessingExecutionOnly`)
      ).then(() => {
        closeConfirmation()
      })
      return
    }

    const genericDialogKey = enumValueToKey(ConfirmationDialogs, genericConfirmation)
    await getConfirmation(
      genericConfirmation,
      `${t(`confirmDialogs:${warningDialogKey}.messageProcessingExecution`)}\n\n${t(`confirmDialogs:${genericDialogKey}.message`)}`
    ).then(() => {
      closeConfirmation()
    })
  }

  return { confirmSettlementsBatchLaunch, confirmMassiveBatchLaunch }
}
